<script lang='ts'>
    import { getContext, onDestroy, onMount, tick } from 'svelte';
    import CalendarCard from '../Calendar/CalendarCard.svelte';
    import { addDays, daysBetween, LocalDate, today } from '@/time';
    import { CalendarLoader, type LoaderActivity } from './CalendarLoaderNew';
    import StatsRow from './StatsRow.svelte';
    import LoadingAnimation from './LoadingAnimation.svelte';
    import { findModifiedDates, getRequest, throttleTimeout, debouncer, getForecastResultsForChart } from '@/Util/utils';
    import type { MedianAndWeatherStores, MyFitnessStores } from '../stores';
    import CalendarAddModal from './CalendarAddModal.svelte';
    import CalendarSettingsModal from './CalendarSettingsModal.svelte';
    import CalendarNotesModal from './CalendarNotesModal.svelte';
    import { writable } from 'svelte/store';
    import { addMonths, addWeeks, differenceInCalendarDays, differenceInDays, endOfDay, endOfMonth, format, fromUnixTime, isSameMonth, startOfDay, startOfMonth, subMonths } from 'date-fns';
    import AdaptForecastDialog from './AdaptForecastDialog.svelte';
    import StatsChart from './StatsChart.svelte';
    import StatsTable from './StatsTable.svelte';
    import XfaiBall from '../../Common/XfaiBall.svelte';
    import Am5Loader from '../../Util/Am5Loader.svelte';
    import type { Availability } from '@/Common/constants';
    import { Activity } from '@/Common/ActivityFunctions';
    import { dropzone, dragInformation, DragType } from '@/Common/DragAndDrop';
    import {
        DateFormatter,
        type DateValue,
        getLocalTimeZone
    } from "@internationalized/date";
    import { cn } from "$lib/utils.js";
    import { Button } from "$lib/components/ui/button/index.js";
    import { Calendar } from "$lib/components/ui/calendar/index.js";
    import * as Popover from "$lib/components/ui/popover/index.js";
    import DayReviewSheet from './DayReviewSheet.svelte';
    import type { XSSTargetsResponse } from '@/Util/api';

    export let likeScore: number;
    export let dislikeScore: number;
    export let program_weeks;
    export let challenge_days;
    export let sport_type;
    export let showStatsChart;
    export let xssTargetsResponse: XSSTargetsResponse;

    enum BaseView {
        Calendar,
        Chart,
    }

    // NOTE: This has to start from Calendar, or it will never load (there will be races)
    let currentView: BaseView = BaseView.Calendar;

    class ScrollAnimation {
        tempRows: number;
        isDirectionUp: boolean;
        scrollOffsetPx: number;
        targetRow: number;
        fromRow: number;
        inProgress: boolean;
        public constructor(tempRows: number, isDirectionUp: boolean, scrollOffsetPx: number, targetRow: number, fromRow: number) {
            this.tempRows = tempRows;
            this.isDirectionUp = isDirectionUp;
            this.scrollOffsetPx = scrollOffsetPx;
            this.targetRow = targetRow;
            this.fromRow = fromRow;
            this.inProgress = false;
        }
    }
    export let username;
    export let activity_types;
    export let athlete, workout_description;

    const myFitness = getContext<MyFitnessStores>('myFitness')
    myFitness.selectedTrainingPlan = writable(null) // NOTE: myFitness used to be a shared variable, but the context isn't overwritten by changing it here so its fine now
    // const myFitness = {
    //     ...myFitnessContext,
    //     selectedTrainingPlan: writable(null)
    // }
    const { trainingMedians, weather, locationPermission } = getContext<MedianAndWeatherStores>('medianAndWeather')

    const { trainingPlan, recommendedTrainingUpdatedAt, cal_settings } = myFitness

    let statsUpdatedAt = new Date();
    const checkAndReloadStats = () => {
        if ($recommendedTrainingUpdatedAt > statsUpdatedAt) {
            console.log('Reloading stats due to recommendedTrainingUpdatedAt...')
            reloadStats();
            statsUpdatedAt = new Date()
        }
    }
    $: $recommendedTrainingUpdatedAt, checkAndReloadStats()

    let isForecastPlan: boolean
    $: if ($trainingPlan) {
        isForecastPlan = $trainingPlan.program_type === 'targetEvent' && ($trainingPlan.selected_mode === 'target_xss' || $trainingPlan.selected_mode === 'target_focus' || $trainingPlan.selected_mode === 'target_race')
    }
    let forecastResults: any | null = null;
    let forecastStartDate: Date | null = null;
    let forecastEndDate: Date | null = null;
    let hasConflictDays = false
    const updateForecastResults = async (trainingPlan: any) => {
        const tpForecastResults = trainingPlan?.forecastResults ?? null
        if (tpForecastResults) {
            const result = await getForecastResultsForChart(tpForecastResults, trainingPlan.settings ?? null, $cal_settings, $trainingMedians, true)
            forecastResults = result.forecastResults

            // This does not fully capture avail changes since its not reactive, only carries conflict information.
            // Therefore, we still need the other reactive findModifiedDates call
            hasConflictDays = !result.isForecastUpToDate

            forecastStartDate = startOfDay(fromUnixTime(forecastResults.days[0].t))
            forecastEndDate = endOfDay(fromUnixTime(forecastResults.days[forecastResults.days.length - 1].t))
        } else {
            hasConflictDays = false
            forecastStartDate = null
            forecastEndDate = null
        }
    }
    $: updateForecastResults($trainingPlan)

    let forecastResultsMap = {}
    function updateForecastResultsMap() {
        forecastResultsMap = {}
        for(let day of forecastResults.days) {
            if (day.fixed !== true) {
                const dayDate = fromUnixTime(day.t)
                const dateString = LocalDate.format(dayDate)
                forecastResultsMap[dateString] = day
            }

        }
    }
    $: if (forecastStartDate && forecastEndDate && forecastResults) updateForecastResultsMap()


    let notesUpdate;
    function openCalendarNotesModal(event) {
        notesUpdate.initNotesModal(event);
    }

    let calendarDayReviewSheetOpen: boolean = false;
    let calendarDayReviewSheetDate: Date = new Date();

    //@ts-ignore
    const jq = window.$;
    //@ts-ignore
    const Xert = window.Xert;

    const daysShort = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    let calendarSize: ResizeObserverSize[] = [{blockSize: 0, inlineSize: 0} as ResizeObserverSize];
    let headerSize: ResizeObserverSize[] = [{blockSize: 0, inlineSize: 0} as ResizeObserverSize];
    // let calendarWidth: number = 0;
    // let calendarHeight: number = 0;
    let calendar: HTMLElement | null = null;
    let scrollAnimation: ScrollAnimation = null; // Used for the scrolling animation
    let scrollDirectionUp: boolean = true;
    let scrollNeedsUpdate: boolean = false;
    // The number of extra rows rendered above and below the rows in the view
    const paddingRows: number = 3;
    let topRow: number = -paddingRows;
    let firstVisibleRow: number = paddingRows;

    let firstVisibleDay = addDays($today, -$today.getDay());
    let root: HTMLElement;
    let calendarViewType: 'grid' | 'list' = 'grid';
    let isCompactView = false;
    let CalendarAddModalElement;
    let calSettingsModal;
    let adaptForecastModal;
    let indexOffset = 0;//This shifts by 1 if the starting day is set as monday

    const hoverQuery = window.matchMedia('(hover: hover)');
    let isTouchDevice = !hoverQuery.matches;
    hoverQuery.addEventListener('change', (e) => isTouchDevice = !e.matches);

    $: indexOffset = $cal_settings?.start_of_week ? $cal_settings.start_of_week : 0;

    // TODO: Is this needed? Row resize observer might be a better place for this code.
    async function updateMobileView(prevViewType, scrollToDate: Date = null) {
        const calendarWidth = headerSize[0].inlineSize
        if (calendarWidth <= 0 || calendarSize[0].blockSize <= 0) {
            // console.error('updateMobileView but calendarWidth is 0!')
            return
        }

        isCompactView = calendarWidth < 900;
        if (!isCompactView) {
          calendarViewType = 'grid';
        }

        if (calendarViewType != prevViewType) {
            if (scrollToDate) {
              topRow = dateToIndex(scrollToDate, calendarViewType == 'list') - paddingRows
            } else {
              topRow = prevViewType == 'list' ?
                  Math.floor((topRow + firstVisibleRow) / 7) - paddingRows :
                  (topRow + firstVisibleRow) * 7 - paddingRows;
            }
            // console.log(`topRow updated due to isMobile!`, {topRowInView, topRow, firstVisibleRow})
            updateRows(true) // Force rebuild of calendar rows
            let firstRow = document.getElementById('row' + (topRowInView + paddingRows));
            if (firstRow) {
                firstRow.scrollIntoView();
            }
        }

        // Rows built while the tab was hidden have no `top` set — position them now.
        if (rows.length > 0) {
            await tick();
            const anchorRow = document.getElementById('row' + (topRowInView + firstVisibleRow));
            if (anchorRow && !anchorRow.style.top) {
                updateAbsolutePositions(topRowInView + firstVisibleRow);
                document.getElementById('row' + (topRowInView + paddingRows))?.scrollIntoView();
            }
        }
    }
    $: if (headerSize && calendarSize && currentView == BaseView.Calendar) updateMobileView(calendarViewType)

    // jq(document).ready(function() {
    //     calendarWidth = document.getElementById('calendar').clientWidth;
    // });

    // InView variables are used to render the view
    // They are not updated to match the real values until the necessary data is loaded.
    let numRowsInView = 0;
    let topRowInView = topRow;
    let isLoading = true;

    let loader = new CalendarLoader(onActivitiesChange, onStatsChange);
    let loadedActivities: { [date: string] : LoaderActivity[] } = {};
    let loadedStats = {};
    let loadedTrainingStatuses = {};

    let activitiesRequireUpdate = false;
    let statsRequireUpdate = false;
    function onActivitiesChange() {
        if (scrollAnimation) {
            activitiesRequireUpdate = true;
        } else {
            activitiesRequireUpdate = false;
            isLoading = false;
            loadedActivities = loader.getActivities()
            myFitness.loadCommentCounts(
                Object.values(loadedActivities).flat()
                    .filter((la) => Activity.isActivity(la.activity))
                    .map((la) => la.activity.path)
                    .filter((p): p is string => !!p)
            );
            if (!rows.length)
                updateRows();
        }
    }
    function onStatsChange() {
        if (scrollAnimation) {
            statsRequireUpdate = true;
        } else {
            statsRequireUpdate = false;
            [loadedStats, loadedTrainingStatuses] = loader.getStats();
        }
    }

    let lastForecastInputs : null | Availability[] = null

    let availableStats = [
        { value: 'total_time', label: 'Hours'},
        { value: 'total_distance', label: 'Distance'},
        { value: 'xss', label: 'XSS/day'},
        { value: 'ramp_rate', label: 'Ramp Rate'},
        { value: 'xss_focus', label: 'Weekly Focus'},
        { value: 'polarity_ratio', title: 'Ratio of Low Intensity Strain to High + Peak', label: 'Intensity Ratio'},
        { value: 'tl', title: 'Training Load', label: 'TL'},
        { value: 'ftp', title: 'Threshold Power', label: 'TP'},
        { value: 'hie', title: 'High Intensity Energy', label: 'HIE'},
        { value: 'pp', title: 'Peak Power', label: 'PP'},
        { value: 'ltp', title: 'Lower Threshold Power', label: 'LTP'},
        { value: 'athlete_fp', label: athlete + ' ' + workout_description},
        { value: 'xss_spr', label: 'Specificity'},
        { value: 'ltl', title: 'Low Training Load', label: 'LTL'},
        { value: 'htl', title: 'High Training Load', label: 'HTL'},
        { value: 'ptl', title: 'Peak Training Load', label: 'PTL'},
        { value: 'xss_week', title: 'Weekly Total XSS', label: 'Total XSS'},
        { value: 'xlss', title: 'Low Strain Score', label: 'XLSS'},
        { value: 'xhss', title: 'High Strain Score', label: 'XHSS'},
        { value: 'xpss', title: 'Peak Strain Score', label: 'XPSS'},
        { value: 'rl', title: 'Recovery Load', label: 'RL'},
        { value: 'form', label: 'Form'},
    ];
    // set preselected stats
    let displayedStats = (JSON.parse( localStorage.getItem( 'DataTables_CalendarWeeklyStats') ?? 'null')) ?? availableStats.slice(0, 5);
    displayedStats.forEach(s => {
        let stat = availableStats.find(({ value }) => value == s.value);
        if (stat) stat['preselected'] = true;
    });
    // update localStorage when displayedStats changes
    $: if (displayedStats) localStorage.setItem( 'DataTables_CalendarWeeklyStats', JSON.stringify(displayedStats) );

    $: {
        const minCellHeight = isCompactView ? 60 : 136;
        // Use window.innerHeight as a fallback when the calendar is hidden (blockSize=0),
        // so the loader fires on page load even before the planner tab is opened.
        const effectiveHeight = calendarSize[0].blockSize > 0 ? calendarSize[0].blockSize : window.innerHeight;
        const numRows = paddingRows + Math.ceil(effectiveHeight / minCellHeight) + 1 + paddingRows;
        let numRowsTemp = numRows + (scrollAnimation ? scrollAnimation.tempRows : 0);
        if (numRowsTemp > 2*paddingRows + 1) {
            let start, end;
            if (scrollAnimation) {
                // only load the exercises at the destination
                start = indexToDate(scrollAnimation.isDirectionUp ? -1 : scrollAnimation.tempRows - 1, topRow, calendarViewType == 'list');
                end = indexToDate(scrollAnimation.isDirectionUp ? numRowsTemp - scrollAnimation.tempRows + 1: numRowsTemp + 1, topRow, calendarViewType == 'list');
            } else {
                start = indexToDate(-1, topRow, calendarViewType == 'list');
                end = indexToDate(numRowsTemp + 1, topRow, calendarViewType == 'list');
            }
            loader.load(start, end);
        }
    }

    /** Call this to reload the stats whenever the day changes or the starting day of the week is changed (indexOffset) */
    function onDayChange() {
        loader.clearStats();
        const low = indexToDate(0, topRowInView, calendarViewType == 'list');
        const high = indexToDate(numRowsInView, topRowInView, calendarViewType == 'list');
        loader.load(low, high);
        updateRows(true) // Force rebuild of calendar rows
    }
    $: indexOffset, $today, onDayChange();


    // Height of virtual scrolling element.
    // Can actually be very large without performance loss.
    const VIRTUAL_SCROLL_BUFFER = 1E6 // 1M pixels
    let virtualScrollContainer: HTMLElement

    function updateAbsolutePositions(scrollRelativeToRow, y0: number|null = null) {       
        let row = document.getElementById(`row${scrollRelativeToRow}`)
        if (row) {
            let y1: number
            if (y0 === null) {
                if (!row.style.top) {
                    y0 = VIRTUAL_SCROLL_BUFFER
                    row.style.top = `${y0.toFixed()}px`;
                    console.log(`row${scrollRelativeToRow} reset to dummyHeight`)
                } else {
                    y0 = row.offsetTop
                }
            } else {
                const top = `${y0.toFixed()}px`;
                row.style.top = top;
            }

            if (y0 <= 0) {
                // console.error('updateAbsolutePositions but y0 is 0!')
                return
            }

            // -1 margin so that in mobile view, training status gradient looks continuous
            const margin = calendarViewType == 'list' ? -1 : 6
            y1 = y0 + row.clientHeight + margin

            let j = scrollRelativeToRow - 1;
            let k = rows.findIndex(({row})=>row == j)
            let y = y0

            while (k >= 0) {
                j = rows[k--].row
                row = document.getElementById(`row${j}`)
                if (row) {
                    y -= row.clientHeight + margin
                    let top = `${y.toFixed()}px`;
                    if (top != row.style.top)
                        row.style.top = top;
                }
            }
            j = scrollRelativeToRow + 1;
            k = rows.findIndex(({row})=>row == j)
            y = y1
            while (k < rows.length) {
                if (rows && rows[k]?.row != null) {
                    j = rows[k].row
                    row = document.getElementById(`row${j}`)
                    if (row) {
                        let top = `${y.toFixed()}px`;
                        if (top != row.style.top)
                            row.style.top = top;
                        y += row.clientHeight + margin
                    }
                }
                k += 1;
            }
        } else {
            console.error(`row${scrollRelativeToRow} didn't exist when updateAbsolutePositions called`)
        }
        scrollNeedsUpdate = false;
    }

    let rowResizeObserver = new ResizeObserver(()=>{
        if (!calendar) return

        const calendarWidth = headerSize[0].inlineSize
        if (calendarWidth <= 0) {
            // console.error('rowResizeObserver but calendarWidth is 0!')
            return
        }

        // updateFirstVisibleDay()
        if (scrollAnimation) {
            const row = document.getElementById('row' + scrollAnimation.targetRow)
            if (!row) {
                console.error('resizeObserver animation, but no row?')
                return
            }
            if (!scrollAnimation.inProgress) {
                updateAbsolutePositions(scrollAnimation.fromRow)
                calendar.scrollTo({top: row.offsetTop, behavior: 'smooth'})
                scrollAnimation.scrollOffsetPx = row.offsetTop
                scrollAnimation.inProgress = true;
            } else {
                updateAbsolutePositions(scrollAnimation.targetRow)
            }
        } else {
            updateAbsolutePositions(topRowInView + firstVisibleRow)
        }

        updateFirstVisibleDay()

        if (calendar.scrollTop == 0) {
            let firstRow = document.getElementById('row' + (topRowInView + paddingRows));
            if (firstRow) {
                firstRow.scrollIntoView();
            }
        }
    })
    let observeRow = (node)=>{
        rowResizeObserver.observe(node)
        return {
            destroy() {
                rowResizeObserver.unobserve(node)
            }
        }
    }
    
    function onWindowResize(event) {
        const height = window.innerHeight - calendar.clientTop
        calendar.style.height = `${height}px`
    }

    let mounted = false
    onMount(function () {
        // bind:this doesn't work properly with beforeUpdate/afterUpdate
        // https://github.com/sveltejs/svelte/issues/6016
        // beforeUpdate sometimes called twice, and second time doesn't have corresponding afterUpdate
        // get elements by id instead
        calendar = document.getElementById('calendar')
        virtualScrollContainer = document.getElementById('virtual-content')
        virtualScrollContainer.style.height = `${(2*VIRTUAL_SCROLL_BUFFER).toFixed()}px`
        calendar.scrollTop = VIRTUAL_SCROLL_BUFFER

        Xert._userChannel.bind('schedule_activity_complete', function(data){
            if (data.refresh_date && data.refresh_date != '') {
                loader.reloadFrom(new Date(data.refresh_date));
            }
        });

        Xert._userChannel.bind('modify_activity_complete', function(data){
            if (data.refresh_date && data.refresh_date != '') {
                loader.reloadFrom(new Date(data.refresh_date));
            }
        });

        Xert._userChannel.bind('delete_activity_complete', function(data){
            if (data.refresh_date && data.refresh_date != '') {
                // NOTE: Today's date is added to expand the range of dates from bulkDelete's notification. 
                //Consider adding a new property to the notification (start & end of range), or creating a seperate unique job for bulkDelete
                loader.reloadFromEarliest([new Date(data.refresh_date), new Date()]);
            }
        });

        Xert._userChannel.bind('upload_activity_complete', function(data){
            if (data.refresh_date && data.refresh_date != '') {
                loader.reloadFrom(new Date(data.refresh_date));
            }
        });

        Xert._userChannel.bind('trickle_complete', function(data){
            if (data.refresh_date && data.refresh_date != '') {
                loader.reloadFrom(new Date(data.refresh_date));
            }
        });
        
        Xert._userChannel.bind('sessions', function(data){
            if (data.refresh_date && data.refresh_date != '') {
                loader.reloadFrom(new Date(data.refresh_date));
            }
        });

        mounted = true

        window.addEventListener("resize", onWindowResize)
        // Run event listener once here so that initial window size is used
        onWindowResize(null)

    })

    onDestroy(function (){
        rowResizeObserver.disconnect()
        window.removeEventListener("resize", onWindowResize)
    })

    

    function handleReloadExercises(event){
        let dates = event.detail.dates;
        loader.reloadFromEarliest(dates);
    }

    // updateRows must be called to place new rows into the calendar.
    // This is done in calendarScroll() and 
    let rows = [];
    // Comment counts for each rendered day's notes-button badge (CalendarCard). Re-runs on every
    // row rebuild, scrolling included -- loadDayCommentCounts only requests the days it hasn't
    // fetched yet, so the days that scrolled into view are all a rebuild actually costs.
    $: if (rows.length) myFitness.loadDayCommentCounts(rows.flatMap((r) => r.days.map((d) => d.dateString)), username);
    let numRowsPrev = 0; // Keep track of previous value of numRows so that it can be re-used when calendar size is 0.
    function updateRows(changed = false) {
        const minCellHeight = isCompactView ? 60 : 136;
        const effectiveHeight = calendarSize[0].blockSize > 0 ? calendarSize[0].blockSize : window.innerHeight;
        let numRows = paddingRows + Math.ceil(effectiveHeight / minCellHeight) + 1 + paddingRows;
        if (calendarSize[0].blockSize <= 0) {
            // console.error('updateRows but calendar size 0!', {numRows, numRowsPrev})
            if (numRowsPrev > 0) numRows = numRowsPrev;
        } else {
            numRowsPrev = numRows;
        }
        // console.log('updateRows!', {screenHeight: screen.height, numRows, isMobile, minCellHeight})
        const n = numRows + (
            scrollAnimation
            ?
            scrollAnimation.tempRows
            :
            0);
        if (n != numRowsInView) {
            numRowsInView = n
            changed = true
        }
        if (topRow != topRowInView) {
            topRowInView = topRow;
            changed = true
        }

        if (changed) {
            rows = [];
            for(let i = 0; i < numRowsInView; ++i) {
                // Note: the i < 1.5*numRows etc is for scrollAnimation so it skips cells in the middle of the range
                if (i < 1.5*numRows || i > numRowsInView - 1.5*numRows) {
                    const firstDate = indexToDate(i, topRowInView, calendarViewType == 'list');
                    let days = [];
                    const n_days = calendarViewType == 'list' ? 1 : 7
                    for (let j = 0; j < n_days; ++j) {
                        const date = addDays(firstDate, j)
                        const dateString = LocalDate.format(date)
                        days[j] = {
                            date,
                            dateString,
                        }
                    }
                    rows.push({
                        i,
                        row: topRowInView + i,
                        firstDate,
                        days
                    })
                }
            }
        }
    }

    let isForecastUpToDate = true
    $: lastForecastInputs = $trainingPlan?.forecastResults?.inputs?.days ?? null
    $: if ($trainingPlan?.isForecastPlan && $cal_settings?.defaults && $cal_settings?.availabilities && lastForecastInputs) {
        // We are in target_focus or target_xss, need to make sure the last forecast's inputs have not been changed
        // If they have, we need to adapt the forecast
        const shouldAdaptAvail = findModifiedDates(lastForecastInputs, $cal_settings.defaults, $cal_settings.availabilities)
        isForecastUpToDate = !shouldAdaptAvail && !hasConflictDays
    } else {
        isForecastUpToDate = true
    }

    function getTrainingStatusColors(date: Date, _) {
        let statuses = loadedTrainingStatuses[LocalDate.format(date)];
        return statuses?.map((status)=>{
            // NOTE: end of day is 23:59:59, not 24:00:00, so the divisor is
            // 1 second less than 24 hours to make the final gradient stop at 100%
            return ((status && status.color) ? status.color : '#D8D8D8')
                + ` ${(status.hour/(0.24 - 1.0/360000.0)).toFixed(1)}% `
        }).join(',') ?? ''

    }

    let scrollingIsStopped = true
    let scrollDetectTimer = null
    let throttleFirstVisibleDay = throttleTimeout(updateFirstVisibleDay, 500)
    function scrollEnd(event) {
        if (scrollDetectTimer) clearTimeout(scrollDetectTimer)
        if (scrollAnimation) {
            document.getElementById(`row${scrollAnimation.targetRow}`)?.scrollIntoView()
        } else if (!scrollingIsStopped) {
            scrollingIsStopped = true
            // Sometimes scrollNeedsUpdate gets stuck?
            scrollNeedsUpdate = false

            // Reset infinite scroll
            updateFirstVisibleDay()
            const row = document.getElementById(`row${topRowInView + firstVisibleRow}`)
            const offset = VIRTUAL_SCROLL_BUFFER - calendar.scrollTop

            // NOTE: modifying scrollTop causes another scroll event, and then another scrollEnd event.
            // prevScroll variable used to prevent running this again since scrollingIsStopped is set to scroll only of scrollTop != prevScroll
            prevScroll = VIRTUAL_SCROLL_BUFFER
            calendar.scrollTop = VIRTUAL_SCROLL_BUFFER
            if (row) {
                // console.log(`Reset scroll by ${offset}; row${topRowInView+firstVisibleRow} ${row.offsetTop} -> ${row.offsetTop + offset}`)
                const y = row.offsetTop + offset;
                updateAbsolutePositions(topRowInView + firstVisibleRow, y)
            }
        }
    }

    let prevScroll = 0
    function calendarScroll (event) {
        if (scrollDetectTimer) clearTimeout(scrollDetectTimer)
        const calendar = event.target;

        if (calendar.scrollTop != prevScroll) {
            scrollingIsStopped = false

            // Wait to see if no more events are dispatched.
            // On supported browsers, scrollend event will work instead (chrome and firefox, not safari yet)
            // https://caniuse.com/mdn-api_element_scrollend_event
            scrollDetectTimer = setTimeout(scrollEnd, 250)
        } else {
            scrollingIsStopped = true
        }
        prevScroll = calendar.scrollTop

        if (scrollAnimation) {
            const calendar = event.target
            // if animation just finished
            const distToEnd = document.getElementById('row' + scrollAnimation.targetRow)?.offsetTop - calendar.scrollTop;
            if (Math.abs(distToEnd) < 5) {
                completeScrollAnimation();
            } else {
                updateFirstVisibleDay();
            }
        } else if (!scrollNeedsUpdate) {

            const calendar = event.target

            // TODO: Is this needed? Few big updates vs more small updates, does it actually make any difference in responsiveness?
            // throttle scrolling updates. This way if scrolling fast, a large chunk of views are updated at once instead of one at a time.
            // if (scrollThrottleTimer == null) {
            //     scrollIsThrottled = true
            //     scrollThrottleTimer = setTimeout(()=>scrollIsThrottled = false, 50)
            //     return
            // } else if (scrollIsThrottled) {
            //     return
            // }
            // scrollThrottleTimer = null

            const firstElement = document.getElementById('row' + (topRowInView))
            const lastElement = document.getElementById('row' + (topRowInView + numRowsInView - 1))
            

            if (firstElement && lastElement) {
                const firstVisibleElement = document.getElementById('row' + (topRowInView + paddingRows))
                const lastVisibleElement = document.getElementById('row' + (topRowInView + numRowsInView - paddingRows - 1))
                const avgCellHeight = (lastElement.offsetTop + lastElement.clientHeight - firstElement.offsetTop)/numRowsInView

                const overscrollUp = (firstVisibleElement.offsetTop - calendar.scrollTop)/avgCellHeight
                const overscrollDown = (calendar.scrollTop + calendar.clientHeight - (lastVisibleElement.offsetTop + lastVisibleElement.clientHeight))/avgCellHeight

                if (overscrollUp > 0) {
                    scrollNeedsUpdate = true;
                    scrollDirectionUp = true;
                    const rows = Math.min(Math.ceil(numRowsInView/2), Math.ceil(overscrollUp))
                    topRow -= rows
                    updateRows();
                } else if (overscrollDown > 0) {
                    scrollNeedsUpdate = true;
                    scrollDirectionUp = false;
                    const rows = Math.min(Math.ceil(numRowsInView/2), Math.ceil(overscrollDown))
                    topRow += rows
                    updateRows();
                }
            }
            throttleFirstVisibleDay.trigger();
            // updateFirstVisibleDay();
        }
    }

    // Tracks the first day of the first fully visible row.
    function updateFirstVisibleDay() {
        // Don't run if calendar is hidden due to stats chart
        if (!calendar || calendar.clientHeight <= 0) return

        for (let i = 0; i < numRowsInView; ++i) {
            let row = document.getElementById('row' + (i + topRowInView));
            if (row && row.offsetTop >= calendar.scrollTop) {
                firstVisibleDay = indexToDate(i, topRowInView, calendarViewType == 'list');
                firstVisibleRow = i;
                break;
            }
        }
    }

    function indexToDate(i, top, isListView) {
        //NOTE: Parameter j (horizontal index) was removed because it was only ever being passed in as 0. 
        //To add it back, ((j + indexOffset) % 7) instead of (indexOffset % 7)
        return isListView ? addDays($today, top + i) : addDays($today, (indexOffset % 7) + 7 * (top + i) - $today.getDay());
    }

    function dateToIndex(date: Date, isListView) {
        const diff = differenceInCalendarDays(date, $today)
        return isListView ? diff : Math.floor((diff + $today.getDay() - (indexOffset % 7) + 7) / 7)
    }

    // returns the first day of the last month
    function lastMonth(date: Date) {
        date.setMonth((date.getMonth() + 11) % 12);
        if (date.getMonth() == 11) {
            date.setFullYear(date.getFullYear() - 1);
        }
        date.setUTCDate(1); // This sets the day of the month
        return date;
    }

    // returns the first day of the next month
    function nextMonth(date: Date) {
        return nextNMonths(date, 1);
    }

    function nextNMonths(date: Date, num: number){
        return addMonths(startOfMonth(date), num)
    }

    function scrollToDate(date: Date) {
        const scrollBy = Math.floor(daysBetween(firstVisibleDay, startOfDay(date))/(calendarViewType == 'list' ? 1 : 7))

        if (currentView == BaseView.Calendar) {
            scrollByWeeks(scrollBy);
        } else {
            displayDate = date;

            // Scroll calendar in the background so it matches the date when you switch to it
            // Reset firstVisibleDay back to paddingRows so it's in the default position.
            topRow += scrollBy + firstVisibleRow - paddingRows;
            firstVisibleRow = paddingRows;

            let row = document.getElementById(`row${topRow + firstVisibleRow}`)
            if (row) row.style.top = "" // Clear scroll position so it reverts to default.
            updateRows(true);

            firstVisibleDay = rows[firstVisibleRow].firstDate
        }
    }

    function scrollByWeeks(weeks: number) {
        if (scrollAnimation || !scrollingIsStopped) {
            return;
        }
        if (calendar) {
            let row = document.getElementById(`row${topRowInView + firstVisibleRow + weeks}`)
            // Target row is nearby enough to scroll directly to it without special animation code.
            if (row) {
                calendar.scrollTo({top: row.offsetTop, behavior: 'smooth'})
                return
            }


            let tempRows, target;
            if (weeks < 0) { // scroll up
                tempRows = Math.max(0, -weeks - (firstVisibleRow - paddingRows));
                target = tempRows > 0 ? paddingRows : firstVisibleRow + weeks;
                topRow -= tempRows;
            } else { // scroll down
                tempRows = Math.max(0, weeks + (firstVisibleRow - paddingRows));
                target = tempRows > 0 ? tempRows + paddingRows : firstVisibleRow + weeks;
            }
            scrollAnimation = new ScrollAnimation(
                tempRows,
                weeks < 0,
                0,
                target + topRow,
                topRowInView + firstVisibleRow
            )
            updateRows();
            scrollNeedsUpdate = true;
        }
    }

    function completeScrollAnimation() {
        if (scrollAnimation) {
            topRow = scrollAnimation.targetRow - paddingRows;

            scrollNeedsUpdate = true;
            scrollAnimation = null;

            if (activitiesRequireUpdate) onActivitiesChange();
            if (statsRequireUpdate) onStatsChange();
            
            updateRows(true);
            updateFirstVisibleDay();
        }
    }

    // TODO (hyde): figure out why this is important. Right now this causes all the exercises to reload when pressing
    // "add to calendar" or "change availability"
    // $: reloadCalendar($cal_settings);
    // function reloadCalendar(cal_settings){
    //     if(!cal_settings)return;
    //     loader.clearExercises();
    //     reloadEvents();
    // }

    async function getNotesUpdates() {
        const notes = await window.fetch(('/calendar/get-notes?' + new URLSearchParams({
            forUser: username
        })), {method: 'GET',
        ...getRequest()});
        return await notes.json();
    }
    let notes = null;
    getNotesUpdates().then(result=>{notes = result;});

    // Days that carry a plan report card: the target date of each plan that has a completion report.
    let planReports = null;
    window.fetch('/calendar/plan-reports?' + new URLSearchParams({forUser: username}), {method: 'GET', ...getRequest()})
        .then(r => r.ok ? r.json() : null).then(result => {planReports = result;}).catch(() => {});

    function openCalendarAddModal(event){
        CalendarAddModalElement.init(event.detail.date, setIsLoading, event.detail.useRecommended, event?.detail?.openTo);
    }

    function openCalendarAddModalEdit(event){
        CalendarAddModalElement.initEdit(event.detail.date, event.detail.exercise, event.detail.setIsLoading, event?.detail?.openTo);
    }

    function openCalendarSettingsModal(event) {
        jq('[data-toggle="tooltip"]').tooltip('hide');
        jq('#cal-settings-btn').removeClass("selected");
        calSettingsModal.initCalendarModal(event);
    }

    let statsChart;
    function onCalendarSettingsChange(event){
        localStorage.setItem('legendToggledStorage', "[]");
        if(statsChart) statsChart.showAllLegend(); 
    }

    // TODO (hyde): remove this event and event handler
    function reloadEvents() {
        if (currentView == BaseView.Chart) return
        const lastVisibleDay = indexToDate(numRowsInView, topRowInView, calendarViewType == 'list');
        loader.load(firstVisibleDay, lastVisibleDay);
    }

    // TODO (hyde): see if we can get rid of this function and event
    function reloadStats() {
        loader.clearStats();
        loader.load();
    }

    const setIsLoading = (value: boolean) => {
        isLoading = value
    }

    const onAdaptClick = () => {
        adaptForecastModal.openModal()
        jq('[data-toggle="tooltip"]').tooltip('hide')
    }

    const toggleViews = () => { 
        clearTimeout(scrollDetectTimer)
        jq('[data-toggle="tooltip"]').tooltip('hide');
        if (currentView == BaseView.Calendar) {
            if (!displayDate) displayDate = new Date()
            currentView = BaseView.Chart;
            if (scrollAnimation) {
                completeScrollAnimation();
            }
            document.body.style.position = '';
            document.body.style.width = '';
            document.body.style.height = '';
            document.body.style.overflow = '';
        } else {
            currentView = BaseView.Calendar;
            document.body.style.setProperty('position', 'fixed', 'important');
            document.body.style.setProperty('width', '100vw', 'important');
            document.body.style.setProperty('height', '100vh', 'important');
            document.body.style.overflow = 'hidden';
        }
        return;
    }
    let displayDate: Date | null = null
    $: if (currentView == BaseView.Calendar) {
        displayDate = addDays(firstVisibleDay, (calendarViewType == 'list' ? 0 : 6))
    }
    $: if (currentView == BaseView.Chart && displayDate) {
        displayDate = startOfMonth(displayDate)
        const startDate = startOfMonth(subMonths(displayDate, 2))
        const endDate = addWeeks(startDate, 20);
        loader.load(startDate, endDate);
    }

    let isSmallHeader = false;
    let hideButtonText = false;
    $: if (headerSize) {
        isSmallHeader = headerSize[0].inlineSize < 665 // 665px
        hideButtonText = headerSize[0].inlineSize < 1000 // 665px
    };

    const pinnedState = 'pinned';
    // TODO (hyde): get rid of this
    async function lockEvent(e) { //TODO: use activityfunctions.ts to standardize pinning activities
        const date_string = e.detail.$activityContext.date_string;
        const path = e.detail.$activityContext.path;
        let activity = loadedActivities[date_string]?.findIndex(obj => obj.activity.path = path);
        if(activity == -1) return;

        loadedActivities[date_string][activity].activity.state = loadedActivities[date_string][activity].activity.state.filter(s => s!=pinnedState);
    }

    const statsIncludesFullMonthOf = (date: Date, stats: any) => {
        const includesStart = !!stats.find(ds => {
                const d = new Date(ds)
                return (
                    Math.abs(differenceInDays(startOfMonth(date), d)) < 7 
                    && isSameMonth(date, d)
                )
            }
        )
        const includesEnd = !!stats.find(ds => {
                const d = new Date(ds)
                return (
                    Math.abs(differenceInDays(endOfMonth(date), d)) < 7 
                    && isSameMonth(date, d)
                )
            }
        )
        return includesStart && includesEnd
    }

    let dragScrollTimeout;
    let dragTimeoutRunning = false;
    let dragScrollAmount;
    const _scrollOnDrag = () => {
        if ($dragInformation) {
            dragTimeoutRunning = true
            
            if (!isCancelDroppable) calendar.scrollBy({ top: dragScrollAmount, left: 0 })
            dragScrollTimeout = setTimeout(_scrollOnDrag, 10)
        } else {   
            dragTimeoutRunning = false
        }
    }
    const scrollOnDrag = (clientX: number, clientY: number) => {
        const rect = calendar.getBoundingClientRect()
        if (clientX >= rect.left && clientX <= rect.right) {
            const distFromTop = clientY - rect.top
            const distFromBottom = window.innerHeight - clientY
            if (distFromTop >= 0 && distFromTop <= 100) {
                dragScrollAmount = distFromTop > 50 ? -5 : -15
                if (!dragTimeoutRunning) {
                    dragScrollTimeout = _scrollOnDrag()
                }
                return
            } else if (distFromBottom >= 0 && distFromBottom <= 100) {
                dragScrollAmount = distFromBottom > 50 ? 5 : 15
                if (!dragTimeoutRunning) {
                    dragScrollTimeout = _scrollOnDrag()
                }
                return
            }
        }
        clearTimeout(dragScrollTimeout)
        dragTimeoutRunning = false
    }

    const df = new DateFormatter("en-US", {
        dateStyle: "long"
    });
    
    let datePickerDate: DateValue | undefined = undefined;
    let datePickerOpen: boolean = false

    $: {
        if (datePickerDate) {
            scrollToDate(datePickerDate.toDate(getLocalTimeZone()))
            datePickerDate = undefined
            datePickerOpen = false
        }
    }

    let isCancelDroppable = false

    let dragElementMap = new Set<HTMLElement>();
    function onDragEnter(e: DragEvent) {
        console.log('calendar drag enter event', e.target, dragElementMap.size)
        if (root.contains(e.target as Node)) {
            dragElementMap.add(e.target as HTMLElement)
        }
        // Clean up elements that were removed before the leave event got to fire.
        for (let elem of dragElementMap) {
            // if (!elem.isConnected) dragElementMap.delete(elem)
            if (!root.contains(elem)) dragElementMap.delete(elem)
        }
        if (dragElementMap.size == 0) return

        // NOTE: Can't access e.dataTransfer.files in dragenter for security reasons.
        // Instead check dataTransfer.types
        // if ($dragInformation == null && e.dataTransfer?.files?.length > 0) {
        if ($dragInformation == null && e.dataTransfer?.types?.includes("Files")) {
            e.dataTransfer.effectAllowed = 'copy';
            // Dragging a file into the calendar.
            dragInformation.set({
                type: DragType.IMPORT
            })
        }
    }
    function onDragLeave(e: DragEvent) {
        dragElementMap.delete(e.target as HTMLElement)
        // Clean up elements that were removed before the leave event got to fire.
        for (let elem of dragElementMap) {
            // if (!elem.isConnected) dragElementMap.delete(elem)
            if (!root.contains(elem)) dragElementMap.delete(elem)
        }

        // If drag leave event occurs and isn't cleared by a dragEnter event,
        // it's likely due to the user stopping the drag/drop operation.
        // console.log('drag leave event', e)
        // console.log('drag leave target', e.target)
        // console.log('drag leave related target', e.relatedTarget)
        // console.log('drag leave effect', e.dataTransfer.dropEffect)
        // NOTE: relatedTarget is null when cancelling drag.
        // When dragging file on top of child elements, a lot of dragleave events will fire, but they will have a non-null relatedTarget.

        // NOTE: Safari doesn't set relatedTarget
        // https://bugs.webkit.org/show_bug.cgi?id=66547
        // Bug was fixed on 2026-03-19, but not in produciton version of Safari yet.
        // let isLeaving = e.relatedTarget == null || !root.contains(e.relatedTarget as Node)
        // let isLeaving = !root.contains(e.target as Node)
        let isLeaving = dragElementMap.size == 0

        if ($dragInformation?.type == DragType.IMPORT && isLeaving) {
            // Dragging a file out of the calendar does not run the dragend from DragAndDrop.ts,
            // so it needs to be cleared here.
            dragInformation.reset()
        }
    }
    function onDrop(e: DragEvent) {
        console.log('document drop target', e.relatedTarget)
        console.log('document drop effect', e.dataTransfer.dropEffect)
        if ($dragInformation?.type == DragType.IMPORT) {
            // Dragging a file out of the calendar does not run the dragend from DragAndDrop.ts,
            // so it needs to be cleared here.
            dragInformation.reset()
        }
        dragElementMap.clear()

    }
    function onDragOver(e: DragEvent) {
    }
</script>

<div id='calendar-header' bind:borderBoxSize={headerSize} class:isSmallHeader class:hideButtonText class='calendar-row flex-wrap justify-end gap-y-2 p-2 md:p-4'>
    {#if displayDate}
        <div class="header-row justify-start relative extra-text">
            <!-- <h3 class="flex-grow font-medium whitespace-nowrap">Fitness Planner</h3> -->
        </div>
        <div id="dateDiv" class="header-row relative no-twp">
            <div class='flex flex-row gap-[4px] items-center'>
                <Button
                    variant='outline'
                    size='sm'
                    disabled={scrollAnimation != null || !scrollingIsStopped}
                    on:click={() => {scrollToDate(new Date())}}
                >
                    Today
                </Button>
                <Popover.Root portal="#shadcn-ui-dialog-target" bind:open={datePickerOpen}>
                    <Popover.Trigger asChild let:builder>
                        <Button
                            variant="ghost"
                            class={cn(
                                "w-[160px] justify-start text-left font-normal py-0 text-lg",
                            )}
                            builders={[builder]}
                        >
                            {format(displayDate, (isSmallHeader ? "MMM '' yy" : 'MMMM yyyy'))}
                        </Button>
                    </Popover.Trigger>
                    <Popover.Content class="w-auto p-0 border-none" align="start">
                        <Calendar bind:value={datePickerDate} />
                    </Popover.Content>
                </Popover.Root>
                <Button variant='ghost' size='icon' on:click={() => scrollToDate(lastMonth(displayDate))}>
                    <i class='fa-thin fa-xl fa-chevron-left' aria-hidden='true'/>
                </Button>
                <Button variant='ghost' size='icon' on:click={() => scrollToDate(nextMonth(displayDate))}>
                    <i class='fa-thin fa-xl fa-chevron-right' aria-hidden='true'/>
                </Button>
            </div>
        </div>
    {/if}
    <div class="header-row justify-end">
        <div class='flex flex-row gap-[4px] items-center'>
            {#if isForecastPlan}
                <Button variant='outline' on:click={onAdaptClick} class='group/crysball inline-flex justify-center items-center' id='adapt-forecast-btn'>
                    <span class='relative px-[2px] pt-1 mr-1'>
                        <XfaiBall class="h-5" type="hover"/>
                        <div class="red-dot" hidden={isForecastUpToDate} />
                    </span>
                    <span class:hideButtonText  class='extra-text2'>Adapt Forecast</span>
                </Button>
            {/if}
            {#if currentView == BaseView.Calendar && isCompactView}
                <Button variant='outline' class='header-btn' id="cal-toggle-view-btn" data-toggle='tooltip' data-original-title={(calendarViewType == 'grid')?"Toggle List View":"Toggle Grid View"}
                  on:click={() => {
                    const prevViewType = calendarViewType;
                    calendarViewType = calendarViewType == 'grid' ? 'list' : 'grid';
                    updateMobileView(prevViewType);
                    jq('[data-toggle="tooltip"]').tooltip('hide');
                  }}
                  disabled={scrollAnimation != null || !scrollingIsStopped}
                >
                    {#if calendarViewType == 'list'}
                        <i class="fa-light fa-lg fa-calendar-days"></i>
                    {:else if calendarViewType == 'grid'}
                        <i class="fa-light fa-lg fa-bars"></i>
                    {/if}
                </Button>
            {/if}
            {#if showStatsChart || true}
                <Button variant='outline' class='header-btn' id="cal-toggle-view-btn" data-toggle='tooltip' data-original-title={(currentView == BaseView.Calendar)?"Toggle Chart View":"Toggle Calendar View"} on:click={toggleViews}>
                    {#if currentView == BaseView.Calendar}
                        <i class="fa-light fa-chart-line"></i>
                    {:else if currentView == BaseView.Chart}
                        <i class="fa-light fa-calendar"></i>
                    {/if}
                </Button>
            {/if}
            <Button variant='outline' class='header-btn' id="cal-settings-btn" data-toggle='tooltip' data-original-title="Settings" on:click={openCalendarSettingsModal} >
                <i class='fa-light fa-gear fa-lg'/>
            </Button>
            <!-- TODO: Implement better scrolling -->
            <!-- <button class='calendar-button ml-4 py-1 px-2' id='fullscreen-toggle-btn' data-toggle='tooltip' data-original-title={isFullScreen ? 'Minimize' : 'Maximize'} on:click={() => fullScreen(!isFullScreen)}>
                <i class='fa-light fa-{isFullScreen ? 'compress' : 'expand'} fa-lg'/>
            </button> -->
        </div>
    </div>
</div>
{#if currentView == BaseView.Calendar && calendarViewType == 'grid'}
    <div id='day-of-week-row' class='calendar-row border border-x-0 border-zinc-300'>
        {#each Array(7) as _, i}
            <div class='day-of-week'>{daysShort[(i + indexOffset) % 7]}</div>
        {/each}
    </div>
{/if}

<!-- TODO (hyde): fix/improve mobile calendar UI -->
<!-- svelte-ignore a11y-no-static-element-interactions -->
<div class="relative"
    bind:this={root}
    on:reload={handleReloadExercises}
    on:dragenter={onDragEnter}
    on:dragleave={onDragLeave}
    on:dragover|preventDefault={onDragOver}
    on:drop|preventDefault={onDrop}
>
    {#if isLoading}
        <div id='loading'> <LoadingAnimation/> </div>
    {/if}
    <div id='calendar'
        class='hidden-scrollbar'
        class:hidden={currentView != BaseView.Calendar}
        bind:borderBoxSize={calendarSize}
        on:scroll={calendarScroll} 
        on:scrollend={scrollEnd}
        on:addToCalendar={openCalendarAddModal} 
        on:editEvent={openCalendarAddModalEdit} 
        on:availabilitySaved={() => updateForecastResults($trainingPlan)}
        on:showNotesUpdate={openCalendarNotesModal}
        on:showDayReview={(event) => {calendarDayReviewSheetOpen = true; calendarDayReviewSheetDate = event.detail.date}}
        on:reloadEvents={reloadEvents}
        on:reloadStats={reloadStats}
    >
        <div id='virtual-content'>

        {#each rows as {row, firstDate, days} (row)}
            <div use:observeRow class={cn('calendar-row-wrapper shadow-[0px_4px_4px_rgba(0,0,0,0.25)] overflow-clip mx-[4px]', calendarViewType == 'list' ? '' : 'border border-zinc-300')} id={'row' + (row).toString()}>
                <div class='calendar-row'>
                    {#each days as {date, dateString}, j (dateString)}
                        <!-- svelte-ignore a11y-click-events-have-key-events -->
                        <!-- svelte-ignore a11y-no-static-element-interactions -->
                        <div class='cell' class:mobile={calendarViewType == 'list'} class:compact={isCompactView}
                          on:click={async () => {
                            if (isCompactView && calendarViewType == 'grid') {
                                calendarViewType = 'list';
                                updateMobileView('grid', date);
                            }
                          }}
                        >
                            <!-- This is where you put calendar cell content. -->
                            <CalendarCard {myFitness} {isTouchDevice} {isCompactView} {calendarViewType} {date} allActivities={loadedActivities[dateString]} cal_settings={$cal_settings}
                                weather={$locationPermission && $weather.days[dateString]} {notes} {planReports} {username} forecastDay={forecastResultsMap[dateString]}
                                allowSummaryCard={!$cal_settings || $cal_settings.use_aggregate_card} onDrag={scrollOnDrag}
                                calendarLoader={loader}
                            />
                            <div class:mobile={calendarViewType == 'list'} class='training-status' style={'background: linear-gradient(' + (calendarViewType == 'list' ? 180 : 90) + 'deg, ' + getTrainingStatusColors(date, loadedTrainingStatuses) + ');'}/>
                        </div>
                    {/each}
                </div>
                {#if firstDate.getDay() == (indexOffset + (calendarViewType == 'list' ? 6 : 0)) % 7}
                    <!-- When isMobile, statsRow should be rendered above the first date it has date for -->
                    <StatsRow isMobile={calendarViewType == 'list'} date={addDays(firstDate, (calendarViewType == 'list' ? 1 : 0))} {displayedStats} {loadedStats} />
                {/if}
            </div>
        {/each}
        <div class="dummy"></div>
        </div>
    </div>
    {#if $dragInformation}
        <!-- svelte-ignore a11y-click-events-have-key-events -->
        <!-- svelte-ignore a11y-no-static-element-interactions -->
        <div class={cn(
                'drop-cancel-zone fixed bottom-[25px] flex items-center justify-center gap-2 cursor-pointer',
                'rounded-full border-2 shadow-xl',
                'transition-all duration-200',
                isCancelDroppable
                    ? 'bg-red-600 border-red-400 text-white px-5 h-[52px] shadow-red-500/40'
                    : 'bg-red-500 border-red-600 text-white w-[50px] h-[50px]',
            )}
            style="left: 50%; transform: translateX(-50%);"
            use:dropzone={{
                on_dropzone() {
                    isCancelDroppable = false
                    if ($dragInformation?.type == DragType.IMPORT) {
                        dragInformation.reset()
                        dragElementMap.clear()
                    }
                },
                on_dragenter() {
                    isCancelDroppable = true
                },
                on_dragleave() {
                    isCancelDroppable = false
                },
            }}
            on:click={() => {
                dragInformation.reset();
            }}
        >
            <i class='pointer-events-none fa-solid fa-ban {isCancelDroppable ? "fa-lg" : "fa-lg"}'/>
            {#if isCancelDroppable}
                <span class='pointer-events-none text-sm font-semibold whitespace-nowrap'>Cancel</span>
            {/if}
        </div>
    {/if}
    <CalendarAddModal bind:this={CalendarAddModalElement} on:lockEvent={lockEvent} {root} {sport_type} {activity_types} {myFitness} {likeScore} {dislikeScore} {program_weeks} {challenge_days} weather={$locationPermission && $weather ? $weather : null} {xssTargetsResponse}/>
    <CalendarNotesModal bind:this={notesUpdate} {myFitness} {username} bind:notes={notes} today={$today} />
</div>

<CalendarSettingsModal bind:this={calSettingsModal} {availableStats} bind:displayedStats={displayedStats} bind:cal_settings={$cal_settings}  {username} {myFitness} on:submitSettings={onCalendarSettingsChange}/> 

<AdaptForecastDialog bind:this={adaptForecastModal} forecastData={lastForecastInputs} today={$today} />

<DayReviewSheet bind:open={calendarDayReviewSheetOpen}
  currentDay={calendarDayReviewSheetDate}
  todaysActivities={{
    activities: loadedActivities ? loadedActivities[LocalDate.format(calendarDayReviewSheetDate)]?.map((item) => item.activity) || [] : [],
    update: (updater) => {
      loadedActivities[LocalDate.format(calendarDayReviewSheetDate)] = updater(loadedActivities[LocalDate.format(calendarDayReviewSheetDate)]?.map((item) => item.activity) || []).map((activity) => {
        return { isStale: false, activity };
      });
      loadedActivities = { ...loadedActivities };
    }
  }}
/>
  
<!-- {#if currentView == BaseView.Chart}
    {#each Object.keys(loadedStats) as datestart}
        {@const date = addDays(new Date(datestart), 1)}
        {#if isSameMonth(displayDate, date)}
            <div class='calendar-row'>
                {#each Array(7) as i}
                    <div class='training-status' style={'background: linear-gradient(90deg, ' + getTrainingStatusColors(addDays(date, i), loadedTrainingStatuses) + ');'}/>
                {/each}
            </div>
            <StatsRow {isMobile} {date} {displayedStats} {loadedStats} />
        {/if}
    {/each}
{/if} -->

{#if currentView == BaseView.Chart}
    {@const loadedStatsDates = Object.keys(loadedStats)}
    {#if statsIncludesFullMonthOf(displayDate, loadedStatsDates)}
        <Am5Loader>
            <StatsChart bind:this={statsChart} stats={loadedStats} displayedStats={displayedStats} range={[subMonths(endOfMonth(displayDate), 2) , addMonths(endOfMonth(displayDate), 1)]} cal_settings={$cal_settings} />
        </Am5Loader>
        <StatsTable range={[subMonths(endOfMonth(displayDate), 2) , addMonths(endOfMonth(displayDate), 1)]} {loadedStats} {displayedStats} />
    {:else}
        <div class='m-auto w-min p-4'>
            <LoadingAnimation />
        </div>
    {/if}
{/if}

<style lang='scss'>
    #calendar-header {
        text-align: left;
        align-items: center;
        .header-row {
            display: flex;
            flex-direction: row;
            flex-grow: 1;
            flex:1;
        }
        // NOTE: Warning, this containment context can cause scroll issues on firefox!
        // container: cal-header / inline-size;
        // @container cal-header (max-width: 665px) {
        
        &.isSmallHeader {
            .extra-text {
                display: none;
            }
            #dateDiv{
                justify-content: left;

                #scroll-to-today-btn{
                    margin-left:0px;
                }
            }
            #dateLabel{
                width:70px;
            }
            
        }
        &.hideButtonText{
            .extra-text2 {
                display: none;
            }
        }
    }
    #day-of-week-row {
        background-color: white;
        padding: 6px 0;
        border-radius: 4px 4px 0px 0px;
    }
    .day-of-week {
        display: inline-block;
        width: calc(100% / 7);
        text-align: center;
        font-size: 14px;
        font-weight: 400;
        color: #3D3D3D;
    }
    #calendar {
        overflow-y: scroll;
        height: 800px;
        position: relative;
        overflow-anchor: none;
    }
    #adapt-forecast-{
        position: relative;
    }
    .calendar-row {
        display: flex;
    }
    .calendar-row-wrapper {
        background: white;
        position: absolute;
        left: 0;
        width: calc(100% - 8px);
    }
    .cell {
        display: flex;
        flex-direction: column;
        width: calc(100% / 7);
        min-height: 136px;
    }
    .cell.compact {
        min-height: 60px;
    }
    .cell.mobile {
        // This is to position the training status at the start of the cell
        flex-direction: row;
        width: 100%;
        margin-bottom: 0;
    }
    .training-status {
        height: 6px;
        width: 100%;
    }
    .training-status.mobile {
        height: 100%;
        width: 8px;
    }

    #dateDiv{
        //position:absolute;
        //left:42%;
        // justify-content:right;
        // justify-self:right;
        display: flex;
        justify-content: center;
        align-items: center;
        // margin-left: 15%;
    }

    #dateLabel{
        display:flex;
        text-align: left;
        margin-left: 10px;
        width:160px;
    }

    #scroll-to-today-btn{
        width: 55px;
        margin-left: 100px;
        margin-right:10px;
        display: flex;
        justify-content: center; /* Center horizontally */
        align-items: center; /* Center vertically */
    }
    .date-shift-btn{
        margin-left: 5px;
        margin: 5px;
    }

    .header-btn{
        width:  40px;
    }

    .justify-start{
        justify-self:left;
    }

    h3 {
        font-size: 20px;
        margin: 0;
        color: #3D3D3D;
    }
    .calendar-button {
        background-color: #fff;
        border: 1px solid #3D3D3D;
        border-radius: 3px;
        font-size: 16px;
        color: #3D3D3D;
        margin-left: 16px;
        padding: 4px 8px;
        &.isMobile {
            margin-left: 6px;
        }
        &:focus-visible {
            background-color: #e6e6e6;
        }

        // Only do a hover effect if mouse cursor available,
        // otherwise the effect is sticky on mobile.
        @media (pointer: fine) {
            &:hover {
                background-color: #e6e6e6;
            }
        }
        &:active {
            background-color: #d4d4d4;
        }

    }


    .icon-button {
        padding: 0;
        background-color: transparent;
        border: none;
    }
    #loading {
        position: absolute;
        display: flex;
        justify-content: center;
        align-items: center;

        // Allow clicking/scrolling while loading overlay is active.
        pointer-events: none;

        padding-top: 20px;
        top: 0;
        left: 0;
        bottom: 0;
        right: 0;
        opacity: 0.5;
        z-index: 1000;
        background-color: #FFF;
    }
    .hidden-scrollbar {
        -ms-overflow-style: none; /* for Internet Explorer, Edge */
        scrollbar-width: none; /* for Firefox */
    }
    .hidden-scrollbar::-webkit-scrollbar {
        display: none; /* for Chrome, Safari, and Opera */
    }
    .red-dot {
        width: 8px;
        height: 8px;
        border-radius: 50%;
        background-color: red;
        position: absolute;
        top: 2px;
        right: 2px;
    }
    .dummy {
        height: 4096px
    }
</style>
